<template>
  <div class="sidebar-recipients">
    <FriedMessage v-if="signingOrderMessage">{{ signingOrderMessage }}</FriedMessage>
    <FriedRoundedButton
      v-if="showInvite"
      class="invite-button"
      data-external="recipents-modal-invite-button"
      icon-align-right
      @click="$emit('invite')"
    >
      <template #icon>
        <FriedIcon icon="plus-large" />
      </template>
      {{ $t('invite-recipient') }}
    </FriedRoundedButton>
    <template v-for="recipient in recipients" :key="recipient.id">
      <RecipientCard
        :recipient="recipient"
        :enable-transfer="false && isTransferEnabled"
        :is-signing-order="isSigningOrder"
        :recipient-ssn="recipientSsn(recipient.id)"
      />
    </template>
  </div>
</template>

<script lang="ts">
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAsyncState } from '@vueuse/core';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { DocumentService } from '../../../api/documents/services/document.service';
import RecipientCard from './RecipientCard.vue';

export default defineComponent({
  components: { RecipientCard },
  props: {
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: undefined,
    },
    recipientsTurnToSign: {
      type: Object as PropType<SigningPageRecipient>,
      default: undefined,
    },
    showInvite: Boolean,
    isSigningOrder: Boolean,
    currentRecipientsTurnToSign: Boolean,
    isTransferEnabled: Boolean,
  },
  emits: ['invite'],
  setup(props) {
    const { t } = useI18n();

    const signingOrderMessage = computed(() => {
      if (!props.isSigningOrder) {
        return '';
      }
      if (props.currentRecipientsTurnToSign) {
        return t('your-turn-to-sign');
      }
      if (!props.recipientsTurnToSign) {
        return t('other-turn-to-sign-hidden');
      }
      return t('other-turn-to-sign', { name: props.recipientsTurnToSign.fullName });
    });

    const {
      state: recipientsSsn,
      isReady: ssnFetched,
      execute: loadRecipientsSsn,
    } = useAsyncState(DocumentService.getRecipientsSSN(), null);

    const recipientSsn = (recipientId: string) =>
      ssnFetched.value
        ? recipientsSsn.value.find(ssnObject => ssnObject.recipientId === recipientId)?.ssn
        : undefined;

    onMounted(() => {
      loadRecipientsSsn();
    });

    return {
      Placement,
      signingOrderMessage,
      RecipientRole,
      recipientSsn,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
.sidebar-recipients {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);

  .invite-button {
    margin: 0 0 0 auto;
  }

  .recipient-card {
    padding: var(--spacing-100);
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--spacing-100);

    &-info {
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
